import React, { useState } from "react";
import { useI18n } from "../i18n";
import Newsletter from "../components/site/Newsletter";
import { ArrowRight, Database, Layers, Eye, ShieldCheck, MapPin, MessageSquare, ChevronDown, CheckCircle2 } from "lucide-react";

const Home = () => {
  const { t } = useI18n();
  const hero = t("hero");
  const about = t("about");
  const how = t("how");
  const benefits = t("benefits");
  const newsletter = t("newsletter");
  const faq = t("faq");

  const benefitIcons = [Eye, ShieldCheck, ArrowRight, Layers, MapPin, MessageSquare];

  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden bg-[#0B1120]">
        <div className="section-grid absolute inset-0 opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-400 mb-5">
                <span className="h-1.5 w-1.5 bg-emerald-400 rounded-full" />
                {hero.eyebrow}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
                {hero.title}
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
                {hero.subtitle}
              </p>
              <ul className="mt-8 space-y-3 max-w-2xl">
                {hero.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300 text-[15px]">
                    <CheckCircle2 size={18} className="mt-1 text-emerald-500 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#newsletter"
                  data-testid="hero-cta-button"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                >
                  {hero.primaryCta} <ArrowRight size={16} />
                </a>
                <a
                  href="#how"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm font-semibold"
                >
                  {hero.secondaryCta}
                </a>
              </div>
              <p className="mt-6 text-xs uppercase tracking-widest font-mono text-slate-500">
                {hero.trustBadge}
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-2xl bg-[#0F172A]">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/dashboard.jpg`}
                  alt={hero.imageAlt}
                  loading="eager"
                  className="w-full h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1120]/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 bg-[#0B1120]/85 backdrop-blur-sm border border-slate-700 rounded-lg px-4 py-3 flex items-center gap-3">
                  <Database size={18} className="text-emerald-400" />
                  <span className="text-xs font-mono uppercase tracking-widest text-slate-300">
                    {t("common.brandTagline")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" data-testid="about-section" className="bg-[#0F172A] border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-slate-800">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/team.jpg`}
                  alt={about.imageAlt}
                  loading="lazy"
                  className="w-full h-[420px] object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">
                {about.eyebrow}
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                {about.title}
              </h2>
              <p className="mt-4 text-slate-300 text-lg leading-relaxed max-w-2xl">{about.lead}</p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {about.points.map((p, i) => (
                  <div key={i} className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
                    <h3 className="text-base font-semibold text-white">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" data-testid="how-section" className="bg-[#0B1120]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <div className="max-w-3xl">
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">
              {how.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
              {how.title}
            </h2>
            <p className="mt-4 text-slate-300 text-lg leading-relaxed">{how.lead}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {how.steps.map((s, i) => (
              <div
                key={i}
                data-testid={`how-step-${i + 1}`}
                className="rounded-xl border border-slate-800 bg-[#0F172A] p-6 flex flex-col"
              >
                <span className="font-mono text-emerald-400 text-sm">{s.number}</span>
                <h3 className="mt-3 text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" data-testid="benefits-section" className="bg-[#0F172A] border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">
                {benefits.eyebrow}
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                {benefits.title}
              </h2>
              <p className="mt-4 text-slate-300 text-lg leading-relaxed">{benefits.lead}</p>
              <div className="mt-8 rounded-2xl overflow-hidden border border-slate-800">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/metrics.jpg`}
                  alt={hero.imageAlt}
                  loading="lazy"
                  className="w-full h-[260px] object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.items.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <div
                    key={i}
                    data-testid={`benefit-card-${i + 1}`}
                    className="rounded-xl border border-slate-800 bg-[#0B1120] p-5"
                  >
                    <div className="h-10 w-10 rounded-md bg-emerald-600/15 border border-emerald-600/30 flex items-center justify-center">
                      <Icon size={18} className="text-emerald-400" />
                    </div>
                    <h3 className="mt-4 text-base font-semibold text-white">{b.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{b.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" data-testid="newsletter-section" className="bg-[#0B1120]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">
                {newsletter.eyebrow}
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                {newsletter.title}
              </h2>
              <p className="mt-4 text-slate-300 text-lg leading-relaxed">{newsletter.subtitle}</p>
              <div className="mt-6 rounded-2xl overflow-hidden border border-slate-800">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/quito.jpg`}
                  alt={hero.imageAlt}
                  loading="lazy"
                  className="w-full h-[220px] object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <Newsletter />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="bg-[#0F172A] border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <div className="text-center">
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">
              {faq.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
              {faq.title}
            </h2>
          </div>
          <div className="mt-10 flex flex-col gap-3">
            {faq.items.map((it, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={i}
                  data-testid={`faq-accordion-item-${i + 1}`}
                  className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    data-testid={`faq-question-${i + 1}`}
                    className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4 text-slate-100 hover:bg-slate-900/40"
                  >
                    <span className="text-[15px] sm:text-base font-medium">{it.q}</span>
                    <ChevronDown
                      size={18}
                      className={`text-slate-400 shrink-0 ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  {open && (
                    <div className="px-5 sm:px-6 pb-5 text-sm text-slate-400 leading-relaxed">
                      {it.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
